import React, { useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import SourceSelector from './steps/SourceSelector';
import ConnectionStep from './steps/ConnectionStep';
import MappingStep from './steps/MappingStep';
import ResourceMappingStep, { ResourceMap } from './steps/ResourceMappingStep';
import PreviewStep from './steps/PreviewStep';
import RunStep from './steps/RunStep';
import { ImportSourceType } from '../../services/importService';

interface Props {
  pca: IPublicClientApplication;
  onDone: () => void;
}

type StepKey = 'source' | 'connect' | 'mapping' | 'resources' | 'preview' | 'run';

const STEP_LABEL: Record<StepKey, string> = {
  source: 'Source', connect: 'Connect', mapping: 'Mapping',
  resources: 'Resources', preview: 'Preview', run: 'Import',
};

// The Resource Mapping step applies to every non-M365 source: those import
// assignees as plain text. Planner resolves real user ids itself, and
// Project Online is an M365 source.
function stepsFor(source: number | null): StepKey[] {
  const nonM365 = source != null
    && source !== ImportSourceType.Planner
    && source !== ImportSourceType.ProjectOnline;
  return nonM365
    ? ['source', 'connect', 'mapping', 'resources', 'preview', 'run']
    : ['source', 'connect', 'mapping', 'preview', 'run'];
}

export default function ImportWizard({ pca, onDone }: Props) {
  const [step, setStep]                     = useState(0);
  const [source, setSource]                 = useState<number | null>(null);
  const [connectionData, setConnectionData] = useState<any>(null);
  const [mapping, setMapping]               = useState<Record<string, string>>({});
  const [resourceMap, setResourceMap]       = useState<ResourceMap>({});
  const [targetProjectId, setTargetProjectId] = useState<string | null>(null);

  const steps = stepsFor(source);
  const current: StepKey = steps[Math.min(step, steps.length - 1)];
  const next = () => setStep(s => Math.min(s + 1, steps.length - 1));

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
        {steps.map((k, i) => {
          const active = current === k;
          return (
            <span
              key={k}
              style={{
                padding: '4px 10px',
                borderRadius: 'var(--r-sm)',
                background: active ? 'var(--brand-mist)' : 'var(--surface-2)',
                color: active ? 'var(--brand)' : 'var(--ink-3)',
                fontSize: 12,
                fontWeight: active ? 500 : 400,
                fontFamily: 'var(--font-ui)',
              }}
            >
              {i + 1}. {STEP_LABEL[k]}
            </span>
          );
        })}
      </div>

      {step > 0 && current !== 'run' && (
        <button className="btn-ghost" onClick={() => setStep(step - 1)} style={{ marginBottom: 12 }}>
          ← Back
        </button>
      )}

      {current === 'source' && (
        <SourceSelector onSelect={s => { setSource(s); setResourceMap({}); setStep(1); }} />
      )}

      {current === 'connect' && source != null && (
        <ConnectionStep pca={pca} source={source} onConnect={d => { setConnectionData(d); next(); }} />
      )}

      {current === 'mapping' && source != null && (
        <MappingStep source={source} connectionData={connectionData} onContinue={m => { setMapping(m); next(); }} />
      )}

      {current === 'resources' && source != null && (
        <ResourceMappingStep
          pca={pca}
          connectionData={connectionData}
          mapping={mapping}
          initial={resourceMap}
          onContinue={m => { setResourceMap(m); next(); }}
        />
      )}

      {current === 'preview' && source != null && (
        <PreviewStep
          pca={pca}
          source={source}
          connectionData={connectionData}
          mapping={mapping}
          targetProjectId={targetProjectId}
          onSetProject={setTargetProjectId}
          onBeginImport={next}
        />
      )}

      {current === 'run' && source != null && targetProjectId && (
        <RunStep
          pca={pca}
          source={source}
          connectionData={connectionData}
          mapping={mapping}
          resourceMap={resourceMap}
          targetProjectId={targetProjectId}
        />
      )}
    </div>
  );
}
